import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// RenameMemoryValueDialog — spec iter151 (Feb 2026).
//
// Modale de renommage rétroactif d'une valeur mémorisée d'un champ
// « Texte (mémoire) ». Récupère un compteur d'impact live via
// GET /document-types/:type_id/field-memory/impact et appelle
// POST /document-types/:type_id/field-memory/rename à la validation.
//
// Props :
//   • open, onOpenChange : contrôle du Dialog
//   • typeId             : id du DocumentType
//   • fieldKey           : clé du champ (ex: "banque")
//   • fieldLabel         : libellé du champ (affichage)
//   • oldValue           : valeur à renommer
//   • existingValues     : liste des valeurs déjà mémorisées (détection
//                          fusion côté UI). Optionnel.
//   • onDone             : callback appelé après succès (utilisé par la
//                          liste de gestion pour rafraîchir).
// ---------------------------------------------------------------------------
export default function RenameMemoryValueDialog({
  open,
  onOpenChange,
  typeId,
  basePath = null,
  fieldKey,
  fieldLabel,
  oldValue,
  existingValues = [],
  onDone,
}) {
  const base = basePath || `/document-types/${typeId}`;
  const scope = typeId || basePath;
  const [newValue, setNewValue] = useState("");
  const [impact, setImpact] = useState(null);   // number | null
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset à l'ouverture.
  useEffect(() => {
    if (open) {
      setNewValue(oldValue || "");
      setImpact(null);
      setSaving(false);
    }
  }, [open, oldValue]);

  // Fetch impact count à l'ouverture.
  useEffect(() => {
    if (!open || !scope || !fieldKey || !oldValue) return;
    let cancelled = false;
    setLoadingImpact(true);
    api
      .get(`${base}/field-memory/impact`, {
        params: { field_key: fieldKey, old_value: oldValue },
      })
      .then((r) => { if (!cancelled) setImpact(r.data?.affected_docs_count ?? 0); })
      .catch(() => { if (!cancelled) setImpact(null); })
      .finally(() => { if (!cancelled) setLoadingImpact(false); });
    return () => { cancelled = true; };
  }, [open, typeId, fieldKey, oldValue]);

  const trimmedNew = (newValue || "").trim();
  const unchanged = !trimmedNew || trimmedNew === (oldValue || "").trim();
  const wouldMerge = !!trimmedNew && existingValues.some(
    (v) => v !== oldValue && v.toLowerCase() === trimmedNew.toLowerCase()
  );

  const submit = async () => {
    if (unchanged || saving) return;
    setSaving(true);
    try {
      const res = await api.post(
        `${base}/field-memory/rename`,
        { field_key: fieldKey, old_value: oldValue, new_value: trimmedNew },
      );
      const n = res.data?.affected_docs_count ?? 0;
      const merged = res.data?.merged_with_existing;
      if (merged) {
        toast.success(
          `Valeur fusionnée avec « ${trimmedNew} » — ${n} document${n > 1 ? "s" : ""} mis à jour`
        );
      } else {
        toast.success(
          `Valeur renommée — ${n} document${n > 1 ? "s" : ""} mis à jour`
        );
      }
      onOpenChange?.(false);
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Renommage impossible.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="rename-memory-dialog"
        style={{ background: "#1B1B1B", border: "1px solid #2A2A2A" }}
      >
        <DialogHeader>
          <DialogTitle className="text-base">Renommer une valeur mémorisée</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="text-xs text-muted-foreground">
            Champ :{" "}
            <span style={{ color: "#EDEDED" }}>{fieldLabel || fieldKey}</span>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              Valeur actuelle
            </div>
            <div
              className="px-3 py-2 rounded-md text-sm"
              style={{ background: "#141414", border: "1px solid #2A2A2A", color: "#B7B7B7" }}
              data-testid="rename-memory-old-value"
            >
              {oldValue}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              Nouvelle valeur
            </div>
            <Input
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Saisir la nouvelle valeur…"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="rename-memory-new-value-input"
              style={{ background: "#141414", border: "1px solid #2A2A2A" }}
            />
          </div>

          {/* Compteur d'impact */}
          <div
            className="rounded-md px-3 py-2 text-xs flex items-start gap-2"
            style={{ background: "rgba(43,87,154,0.10)", border: "1px solid rgba(43,87,154,0.30)" }}
            data-testid="rename-memory-impact"
          >
            <AlertTriangle size={14} className="mt-[1px]" style={{ color: "#5A9EE0" }} />
            <div style={{ color: "#B7B7B7" }}>
              {loadingImpact ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" />
                  Calcul du nombre de documents impactés…
                </span>
              ) : impact === null ? (
                <>Impossible de calculer le nombre de documents impactés.</>
              ) : impact === 0 ? (
                <>Aucun document existant n'utilise cette valeur (la mémoire sera simplement renommée).</>
              ) : (
                <>
                  Cette action mettra à jour{" "}
                  <b style={{ color: "#EDEDED" }}>{impact}</b>{" "}
                  document{impact > 1 ? "s" : ""} existant{impact > 1 ? "s" : ""}.
                </>
              )}
            </div>
          </div>

          {/* Warning fusion */}
          {wouldMerge && (
            <div
              className="rounded-md px-3 py-2 text-xs flex items-start gap-2"
              style={{ background: "rgba(234,164,32,0.10)", border: "1px solid rgba(234,164,32,0.30)" }}
              data-testid="rename-memory-merge-warning"
            >
              <AlertTriangle size={14} className="mt-[1px]" style={{ color: "#EAA420" }} />
              <div style={{ color: "#EAD08A" }}>
                La valeur « {trimmedNew} » existe déjà — les entrées seront
                <b> fusionnées</b>.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={saving}
            data-testid="rename-memory-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={unchanged || saving}
            data-testid="rename-memory-confirm-btn"
            style={{ background: "#2B579A", color: "white" }}
          >
            {saving ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 size={13} className="animate-spin" /> Enregistrement…
              </span>
            ) : (
              "Renommer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
